import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, LANGS } from "../lib/api";
import { useApp } from "../lib/app";
import { CONSENT } from "../lib/legal";
import { Plain } from "../components/Shell";
import { Chips } from "../components/bits";
import { Logo } from "./Login";

export const FREE = ["mornings", "evenings", "weekends"];
export const GOOD = ["outside errands", "cooking", "phone calls", "planning", "shopping", "staying home for deliveries"];
export const RELS = ["husband", "wife", "mother-in-law", "father-in-law", "mother", "father", "son", "daughter", "sibling", "other"];

export function JoinLink() {
  const [sp] = useSearchParams();
  const code = sp.get("code");
  if (code) sessionStorage.setItem("ul_join", code.toUpperCase());
  return <Navigate to="/start" replace />;
}

function ConsentBox({ text, value, onChange }) {
  return (
    <label className="ul-card p-4 flex gap-3 items-start cursor-pointer" data-testid="consent-box">
      <input type="checkbox" data-testid="consent-checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-6 h-6 mt-0.5 accent-[#6A5AE0] shrink-0" />
      <span className="text-[15px]">{text.split("Privacy Policy")[0]}<a href="/legal/privacy" target="_blank" className="text-[#6A5AE0] underline font-bold">Privacy Policy</a> and{" "}
        <a href="/legal/terms" target="_blank" className="text-[#6A5AE0] underline font-bold">Terms of Use</a>{text.split("Terms of Use")[1]}</span>
    </label>
  );
}

function Consent({ onDone }) {
  const [ok, setOk] = useState(false);
  const save = async () => { try { await api.post("/me/consent", { agree: true }); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl font-extrabold">One small thing first 💜</h1>
      <ConsentBox text={CONSENT.signup} value={ok} onChange={setOk} />
      <p className="text-sm text-[#6B6680]">{CONSENT.note}</p>
      <button data-testid="consent-continue-btn" disabled={!ok} onClick={save} className="ul-btn ul-primary w-full">Continue</button>
    </div>
  );
}

function CreateHome({ onDone, back }) {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/homes", { name, city }); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <button onClick={back} className="text-[#6A5AE0] font-bold">← Back</button>
      <h1 className="font-display text-3xl font-extrabold">Create your home 🏡</h1>
      <input data-testid="create-home-name" className="ul-input" placeholder="Home name, e.g. Gaikwad Ghar" value={name} onChange={(e) => setName(e.target.value)} />
      <input data-testid="create-home-city" className="ul-input" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
      <button data-testid="create-home-btn" disabled={!name || busy} onClick={go} className="ul-btn ul-primary w-full">Create home</button>
    </div>
  );
}

function JoinFlow({ initial, needConsent, onDone, back }) {
  const [code, setCode] = useState(initial || "");
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ member_id: null, name: "", relation: "", language: "English", free_on: [], good_at: [] });
  const [ok, setOk] = useState(false);
  const [step, setStep] = useState(0);
  const look = async (c = code) => {
    try { setInfo((await api.get(`/join/${encodeURIComponent(c.trim())}`)).data); setStep(1); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { if (initial) look(initial); }, []); // eslint-disable-line
  const submit = async () => {
    try { await api.post("/join", { ...f, code, agree: true }); sessionStorage.removeItem("ul_join"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="join-flow">
      {back && <button onClick={back} className="text-[#6A5AE0] font-bold">← Back</button>}
      {step === 0 && (<>
        <h1 className="font-display text-3xl font-extrabold">Join a home</h1>
        <input data-testid="join-code-input" className="ul-input uppercase" placeholder="GHAR-1234" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        <button data-testid="join-find-btn" disabled={!code} onClick={() => look()} className="ul-btn ul-primary w-full">Find home</button>
      </>)}
      {step === 1 && info && (<>
        <h1 className="font-display text-3xl font-extrabold">Join {info.home_name}</h1>
        <p className="text-[#6B6680]">Who are you? Tasks already given to you will move to your account.</p>
        <div className="grid gap-2">
          {info.names.map((n) => (
            <button key={n.id} data-testid={`join-name-${n.name.toLowerCase()}`} data-on={f.member_id === n.id} className="ul-chip !min-h-[52px] text-left"
              onClick={() => setF({ ...f, member_id: n.id, name: n.name, relation: n.relation })}>I'm {n.name}{n.relation ? ` (${n.relation})` : ""}</button>
          ))}
          <button data-testid="join-name-new" data-on={f.member_id === null && step === 1 && f.new} className="ul-chip !min-h-[52px]" onClick={() => setF({ ...f, member_id: null, name: "", new: true })}>I'm someone new</button>
        </div>
        {f.new && !f.member_id && (<>
          <input data-testid="join-new-name" className="ul-input" placeholder="Your first name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Chips multi={false} options={RELS} value={f.relation} onChange={(v) => setF({ ...f, relation: v || "" })} testid="join-rel" />
        </>)}
        <button data-testid="join-next-btn" disabled={!f.name} onClick={() => setStep(2)} className="ul-btn ul-primary w-full">Next</button>
      </>)}
      {step === 2 && (<>
        <h1 className="font-display text-3xl font-extrabold">Hi {f.name} 👋</h1>
        <p className="ul-label">Messages in</p>
        <Chips multi={false} options={LANGS} value={f.language} onChange={(v) => setF({ ...f, language: v || "English" })} testid="join-lang" />
        <p className="ul-label">I'm free on</p>
        <Chips options={FREE} value={f.free_on} onChange={(v) => setF({ ...f, free_on: v })} testid="join-free" />
        <p className="ul-label">I'm good at</p>
        <Chips options={GOOD} value={f.good_at} onChange={(v) => setF({ ...f, good_at: v })} testid="join-good" />
        <ConsentBox text={CONSENT.join} value={ok} onChange={setOk} />
        {needConsent && <p className="text-sm text-[#6B6680]">{CONSENT.note}</p>}
        <button data-testid="join-submit-btn" disabled={!ok} onClick={submit} className="ul-btn ul-primary w-full">Ask to join</button>
      </>)}
    </div>
  );
}

function Pending({ home }) {
  const { refresh, logout } = useApp();
  useEffect(() => { const t = setInterval(refresh, 8000); return () => clearInterval(t); }, [refresh]);
  return (
    <div className="text-center pt-10 space-y-4" data-testid="pending-screen">
      <div className="text-6xl">⏳</div>
      <h1 className="font-display text-3xl font-extrabold">Waiting for approval</h1>
      <p className="text-[#6B6680]">We've asked the admin of {home?.name} to approve you. You'll see the home as soon as they do. 💜</p>
      <button className="ul-btn ul-ghost w-full" data-testid="pending-refresh-btn" onClick={refresh}>Check again</button>
      <button className="text-sm text-[#6B6680] underline" onClick={logout}>Log out</button>
    </div>
  );
}

export default function Start() {
  const { me, refresh, logout } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState(null);
  const joinCode = sessionStorage.getItem("ul_join");
  if (!me) return null;
  if (me.state === "ready") return <Navigate to={sessionStorage.getItem("ul_new_home") ? "/setup" : "/"} replace />;
  const doneCreate = async () => { sessionStorage.setItem("ul_new_home", "1"); await refresh(); nav("/setup"); };
  let body;
  if (me.state === "pending") body = <Pending home={me.home} />;
  else if (me.state === "consent" && joinCode) body = <JoinFlow initial={joinCode} needConsent onDone={refresh} />;
  else if (me.state === "consent") body = <Consent onDone={refresh} />;
  else if (mode === "create") body = <CreateHome onDone={doneCreate} back={() => setMode(null)} />;
  else if (mode === "join" || joinCode) body = <JoinFlow initial={joinCode} onDone={refresh} back={() => { sessionStorage.removeItem("ul_join"); setMode(null); }} />;
  else body = (
    <div className="space-y-4">
      <h1 className="font-display text-3xl font-extrabold">Welcome{me.user?.name ? `, ${me.user.name}` : ""} 💜</h1>
      <p className="text-[#6B6680] font-semibold -mt-2" data-testid="welcome-tagline">Don’t carry it alone. Just UnloadIt.</p>
      <button data-testid="start-create-btn" onClick={() => setMode("create")} className="ul-card w-full p-5 text-left flex gap-4 items-center">
        <span className="text-4xl">🏡</span><span><b className="font-display text-xl block">Create a home</b><span className="text-sm text-[#6B6680]">You'll be the home admin</span></span>
      </button>
      <button data-testid="start-join-btn" onClick={() => setMode("join")} className="ul-card w-full p-5 text-left flex gap-4 items-center">
        <span className="text-4xl">🔑</span><span><b className="font-display text-xl block">Join a home</b><span className="text-sm text-[#6B6680]">Enter a code like GHAR-4821</span></span>
      </button>
    </div>
  );
  return (
    <Plain>
      <div className="flex items-center justify-between pt-6 pb-6"><Logo size={44} /><button className="text-sm text-[#6B6680] underline" data-testid="start-logout" onClick={logout}>Log out</button></div>
      {body}
    </Plain>
  );
}
